import React from 'react';
import { useAbility } from '@casl/react';
import {
  ArrowLeft,
  CalendarPlus,
  Loader2,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { useAuth } from '../../context/AuthContext';
import AccessRestricted from '../../components/AccessRestricted';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn } from '../../components/ui/utils';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { performanceApi } from '../../utils/performance-api';
import type {
  MyPerformanceResponse,
  PerformanceGoal,
  PerformanceTeamSummary,
  TeamMemberResponse,
} from '../../types/performance.types';
import {
  formatPerformanceDate,
  scheduleStatusTone,
} from '../../utils/performance';
import {
  CollapsibleSection,
  PerformanceEmptyState,
  PerformancePill,
  PhaseTimeline,
  PlanChecklist,
  WeightingBar,
} from '../../components/performance/PerformancePrimitives';
import { GoalCard } from '../../components/performance/GoalCard';
import { GoalDetailDialog } from '../../components/performance/GoalDetailDialog';
import { NewGoalDialog } from '../../components/performance/NewGoalDialog';
import { ReviewPanel } from '../../components/performance/ReviewPanel';
import { TeamPanel } from '../../components/performance/TeamPanel';
import { TouchpointsPanel } from '../../components/performance/TouchpointsPanel';

type TabValue = 'goals' | 'review' | 'touchpoints';
type PeriodValue = 'mid' | 'eoy';

const daysUntil = (from: string, to: string) =>
  Math.max(0, Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000));

const PlanBanner: React.FC<{ data: MyPerformanceResponse; personName: string }> = ({
  data,
  personName,
}) => {
  const plan = data.plan;
  const schedule = data.schedule;
  if (!plan || !schedule) return null;

  if (plan.status === 'Submitted') {
    return (
      <div className="rounded-[10px] border border-success-border bg-success-bg px-3 py-2 text-sm text-success">
        {personName === data.me.name
          ? `You submitted your goal plan on ${formatPerformanceDate(plan.submittedAt)}.`
          : `${personName}'s goal plan was submitted on ${formatPerformanceDate(plan.submittedAt)}.`}{' '}
        Goals and weights are locked — only HR can unlock it. Progress, key results and notes
        stay editable.
      </div>
    );
  }
  if (plan.reopenedAt) {
    return (
      <div className="rounded-[10px] border border-link/30 bg-selection px-3 py-2 text-sm text-link">
        HR unlocked {personName === data.me.name ? 'your' : `${personName}'s`} goal plan on{' '}
        {formatPerformanceDate(plan.reopenedAt)} — “{plan.reopenReason}”. Make the changes and
        submit again.
      </div>
    );
  }
  if (!plan.canEdit) {
    return (
      <div className="rounded-[10px] border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
        {schedule.phase.key === 'goal' && schedule.today < schedule.goalOpenDate
          ? `Goal setting opens ${formatPerformanceDate(schedule.goalOpenDate)}. You can draft goals then.`
          : `Goal setting closed ${formatPerformanceDate(schedule.goalCloseDate)}. Ask HR to extend the goal-setting window so you can finish your plan.`}
      </div>
    );
  }
  return null;
};

const GoalSummary: React.FC<{
  name: string;
  goals: PerformanceGoal[];
  planStatus?: string;
  planDate?: string | null;
}> = ({ name, goals, planStatus, planDate }) => {
  const active = goals.filter((goal) => goal.status !== 'Withdrawn');
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">{name}'s goals</h2>
          <span className="text-xs text-muted-foreground">
            {active.length} goal{active.length === 1 ? '' : 's'}
          </span>
          {planStatus === 'Submitted' ? (
            <PerformancePill tone="success">Goal plan submitted</PerformancePill>
          ) : planStatus ? (
            <PerformancePill tone="warning">Goal plan {planStatus.toLowerCase()}</PerformancePill>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {planDate ? `Submitted ${formatPerformanceDate(planDate)}.` : ''}
        </p>
      </div>
    </div>
  );
};

const PageSkeleton: React.FC = () => (
  <div className="mx-auto w-full max-w-[1400px] animate-pulse space-y-5">
    <div className="h-9 w-64 rounded-md bg-muted" />
    <div className="h-5 w-96 rounded-md bg-muted" />
    <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="h-16 rounded-[10px] bg-muted" />
      ))}
    </div>
    <div className="h-10 w-72 rounded-full bg-muted" />
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="h-32 rounded-xl bg-muted" />
      ))}
    </div>
  </div>
);

export default function MyPerformancePage() {
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);
  const canRead = ability.can(ACTIONS.READ, RESOURCES.PERFORMANCE_MY_GOALS);

  const [data, setData] = React.useState<MyPerformanceResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = React.useState<string | undefined>();
  const [tab, setTab] = React.useState<TabValue>('goals');
  const [managerMode, setManagerMode] = React.useState<'mine' | 'team'>('mine');
  const [selectedReport, setSelectedReport] = React.useState<PerformanceTeamSummary | null>(null);
  const [teamMember, setTeamMember] = React.useState<TeamMemberResponse | null>(null);
  const [teamLoading, setTeamLoading] = React.useState(false);
  const [period, setPeriod] = React.useState<PeriodValue>('eoy');
  const [selectedGoal, setSelectedGoal] = React.useState<PerformanceGoal | null>(null);
  const [newGoalOpen, setNewGoalOpen] = React.useState(false);
  const [bookingSignal, setBookingSignal] = React.useState(0);
  const [submittingPlan, setSubmittingPlan] = React.useState(false);
  const [teamTouchpoints, setTeamTouchpoints] = React.useState<
    React.ComponentProps<typeof TouchpointsPanel>['touchpoints']
  >([]);

  const load = React.useCallback(async (scheduleId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await performanceApi.my(scheduleId || selectedScheduleId);
      setData(response);
      if (response.schedule) {
        const phase = response.schedule.phase.key;
        if (phase === 'calibration' || phase === 'released') setPeriod('eoy');
      }
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : 'Failed to load My Performance.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedScheduleId]);

  React.useEffect(() => {
    if (!canRead) return;
    load();
    // The initial load is tied to the permission, not to every selected id change.
  }, [canRead]);

  const reload = async () => {
    await load();
    if (selectedReport) await loadReport(selectedReport.user.id);
  };

  const loadReport = async (userId: number) => {
    setTeamLoading(true);
    try {
      const response = await performanceApi.teamMember(userId, data?.schedule?.id);
      setTeamMember(response);
    } finally {
      setTeamLoading(false);
    }
  };

  React.useEffect(() => {
    if (tab !== 'touchpoints' || !data?.canManageTeam) return;
    performanceApi.touchpoints.list(data.schedule?.id).then(setTeamTouchpoints).catch(() => setTeamTouchpoints([]));
  }, [tab, data?.canManageTeam, data?.schedule?.id]);

  if (!canRead) return <AccessRestricted />;

  if (loading && !data) return <PageSkeleton />;

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <p className="font-medium text-foreground">My Performance could not be loaded.</p>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <Button className="mt-4" onClick={() => load()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const schedule = data.schedule;
  const me = data.me;
  const departmentLabel = me.department
    ? `${me.department}${me.departmentCode ? ` (${me.departmentCode})` : ''}`
    : 'your department';

  if (!schedule) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Performance</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Goals, reviews and check-ins for your performance cycle.
          </p>
        </div>
        <PerformanceEmptyState
          title="No performance cycle is active"
          body="HR has not opened a performance schedule yet. You will see your goals and reviews here when it opens."
        />
      </div>
    );
  }

  if (data.participant && !data.participant.included) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Performance</h1>
          <p className="mt-1 text-sm text-muted-foreground">{schedule.name}</p>
        </div>
        <div className="max-w-xl rounded-xl border border-border bg-card p-6">
          <p className="text-base font-semibold">You're not included in this cycle</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {schedule.name} · reason:{' '}
            <span className="font-medium text-foreground">
              {data.participant.exclusionReason || 'Not included'}
            </span>
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            You won't set goals or complete reviews this cycle. Your manager may still hold
            check-ins with you. Contact HR if this looks wrong.
          </p>
        </div>
      </div>
    );
  }

  const plan = data.plan;
  const activeGoals = data.goals.filter((goal) => goal.status !== 'Withdrawn');
  const remainingWeight = plan?.remainingWeight ?? 100;
  const canCreate =
    Boolean(plan?.canEdit) &&
    activeGoals.length < schedule.maxGoals &&
    remainingWeight >= schedule.minWeight;

  const createHint = (() => {
    if (!plan) return 'No goal plan is available for this cycle.';
    if (plan.status === 'Submitted') return 'Goal plan submitted — goals, targets and weights are locked. Ask HR to unlock it.';
    if (schedule.status === 'Closed' || schedule.status === 'Calibration') {
      return 'Goals are read-only once the schedule reaches calibration.';
    }
    if (schedule.status !== 'Active') return 'Goals can only be created while the schedule is Active.';
    if (schedule.today < schedule.goalOpenDate) {
      return `Goal setting opens ${formatPerformanceDate(schedule.goalOpenDate)}.`;
    }
    if (schedule.today > schedule.goalCloseDate) {
      return `Goal setting closed ${formatPerformanceDate(schedule.goalCloseDate)} — ask HR to extend the window.`;
    }
    if (activeGoals.length >= schedule.maxGoals) {
      return `Maximum of ${schedule.maxGoals} goals reached.`;
    }
    if (remainingWeight < schedule.minWeight) {
      return 'No weight left — lower another goal first.';
    }
    if (!plan.canEdit) return 'This goal plan is not editable right now.';
    return '';
  })();

  const goalChip = (() => {
    if (schedule.phase.key === 'goal' && schedule.today < schedule.goalOpenDate) {
      return {
        tone: 'link' as const,
        label: `Goal setting opens ${formatPerformanceDate(schedule.goalOpenDate)}`,
      };
    }
    if (schedule.phase.key === 'goal' && schedule.today <= schedule.goalCloseDate) {
      return {
        tone: 'warning' as const,
        label: `Goal setting closes in ${daysUntil(schedule.today, schedule.goalCloseDate)} days`,
      };
    }
    return {
      tone: 'muted' as const,
      label: `Goal setting closed ${formatPerformanceDate(schedule.goalCloseDate)} · weights frozen`,
    };
  })();

  const viewingTeam = managerMode === 'team';
  const activeReport = viewingTeam ? selectedReport : null;
  const activeTeamMember = selectedReport && teamMember?.user.id === selectedReport.user.id ? teamMember : null;
  const displayGoals = activeReport
    ? activeTeamMember?.goals || []
    : data.goals;
  const displayPlan = activeReport ? activeTeamMember?.plan || null : plan;
  const displayName = activeReport ? activeReport.user.name : me.name;

  const selectReport = async (report: PerformanceTeamSummary, nextTab?: TabValue) => {
    setSelectedReport(report);
    setTab(nextTab || tab);
    await loadReport(report.user.id);
  };

  const backToTeam = () => {
    setSelectedReport(null);
    setTeamMember(null);
  };

  const reviewKey = period;
  const ownReview = data.reviews[reviewKey];
  const teamReview = activeTeamMember?.reviews[reviewKey] || null;
  const reviewPersonId = activeReport ? activeReport.user.id : me.id;
  const reviewPersonName = activeReport ? activeReport.user.name : me.name;

  const periodWindow = period === 'mid' ? schedule.mid : schedule.eoy;
  const periodEnabled = periodWindow.enabled;
  const periodOpen = Boolean(periodWindow.start && schedule.today >= periodWindow.start);

  const updateWeight = async (goalId: number, weight: number) => {
    try {
      await performanceApi.goals.update(goalId, { weight });
      await reload();
    } catch {
      // The API toast carries the message; reload so the field shows the stored weight.
      await reload();
    }
  };

  const submitPlan = async () => {
    if (!plan?.ready) return;
    setSubmittingPlan(true);
    try {
      await performanceApi.plan.submit(schedule.id);
      toast.success('Goal plan submitted and locked');
      await reload();
    } finally {
      setSubmittingPlan(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 md:space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">My Performance</h1>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Select
              value={String(schedule.id)}
              onValueChange={(value) => {
                setSelectedScheduleId(value);
                setSelectedReport(null);
                setTeamMember(null);
                load(value);
              }}
            >
              <SelectTrigger className="h-8 w-auto min-w-[240px] gap-2 bg-card text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {data.schedules.map((cycle) => (
                  <SelectItem key={cycle.id} value={String(cycle.id)}>
                    {cycle.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <PerformancePill tone={scheduleStatusTone(schedule.status)}>
              {schedule.status}
            </PerformancePill>
            <PerformancePill tone={goalChip.tone}>{goalChip.label}</PerformancePill>
          </div>
        </div>
        {me.manager ? (
          <Button
            variant="outline"
            onClick={() => {
              setManagerMode('mine');
              setTab('touchpoints');
              setBookingSignal((value) => value + 1);
            }}
          >
            <CalendarPlus className="mr-2 h-4 w-4" /> Schedule check-in
          </Button>
        ) : null}
      </div>

      <PhaseTimeline timeline={schedule.timeline} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(value) => setTab(value as TabValue)}>
          <TabsList className="justify-start overflow-x-auto">
            <TabsTrigger value="goals" className="flex-none px-3">
              Goals
            </TabsTrigger>
            <TabsTrigger value="review" className="flex-none px-3">
              Review
            </TabsTrigger>
            <TabsTrigger value="touchpoints" className="flex-none px-3">
              Touchpoints
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {data.canManageTeam && data.reports.length > 0 ? (
          <div className="inline-flex rounded-full bg-muted p-1">
            <button
              type="button"
              onClick={() => {
                setManagerMode('mine');
                setSelectedReport(null);
                setTeamMember(null);
              }}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                managerMode === 'mine'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground'
              )}
            >
              My Goals
            </button>
            <button
              type="button"
              onClick={() => setManagerMode('team')}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                managerMode === 'team'
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground'
              )}
            >
              My Team ({data.reports.length})
            </button>
          </div>
        ) : null}
      </div>

      {tab === 'goals' ? (
        viewingTeam && !selectedReport ? (
          <TeamPanel
            reports={data.reports}
            onOpenReport={(report) => selectReport(report, 'goals')}
            onReviewReport={(report) => selectReport(report, 'review')}
          />
        ) : (
          <div className="space-y-4">
            {selectedReport ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={backToTeam}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" /> My Team
                </button>
                <div className="flex items-center gap-2">
                  <PerformancePill tone={displayPlan?.status === 'Submitted' ? 'success' : 'warning'}>
                    Goal plan {displayPlan?.status || 'Draft'}
                  </PerformancePill>
                </div>
              </div>
            ) : null}

            {!viewingTeam ? (
              <>
                <CollapsibleSection
                  title="Company goals"
                  meta={`${data.companyGoals.length} · read-only`}
                  className="border-border bg-selection"
                >
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {data.companyGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className="flex h-full flex-col rounded-[10px] border border-border bg-card p-3"
                      >
                        <p className="text-sm font-semibold leading-snug text-foreground">
                          {goal.title}
                        </p>
                        {goal.description ? (
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {goal.description}
                          </p>
                        ) : null}
                        <p className="mt-auto pt-3 text-xs text-muted-foreground">
                          {goal.ownerName ? `Owner ${goal.ownerName}` : 'No owner'}
                        </p>
                      </div>
                    ))}
                    {!data.companyGoals.length ? (
                      <p className="text-sm text-muted-foreground">No company goals set.</p>
                    ) : null}
                  </div>
                </CollapsibleSection>

                <CollapsibleSection
                  title="Department goals"
                  meta={`${departmentLabel} · read-only`}
                  className="border-border bg-selection"
                >
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {data.departmentGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className="flex h-full flex-col rounded-[10px] border border-border bg-card p-3"
                      >
                        <p className="text-sm font-semibold leading-snug text-foreground">
                          {goal.title}
                        </p>
                        {goal.description ? (
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {goal.description}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-link">
                          ↳ {goal.companyGoalTitle || 'No company goal'}
                        </p>
                        <p className="mt-auto pt-3 text-xs text-muted-foreground">
                          {goal.ownerName ? `Owner ${goal.ownerName}` : 'No owner'}
                        </p>
                      </div>
                    ))}
                    {!data.departmentGoals.length ? (
                      <p className="text-sm text-muted-foreground">
                        No department goals for {departmentLabel}.
                      </p>
                    ) : null}
                  </div>
                </CollapsibleSection>
              </>
            ) : null}

            <GoalSummary
              name={displayName}
              goals={displayGoals}
              planStatus={displayPlan?.status}
              planDate={displayPlan?.submittedAt}
            />

            {displayPlan ? <PlanBanner data={{ ...data, plan: displayPlan }} personName={displayName} /> : null}

            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 md:flex-row md:items-center md:justify-between">
              <WeightingBar goals={displayGoals} className="min-w-0 flex-1" />
              {!selectedReport && plan ? (
                <div className="flex flex-col items-start gap-1 md:items-end">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {remainingWeight}% unallocated
                    </span>
                    <Button
                      size="sm"
                      onClick={() => setNewGoalOpen(true)}
                      disabled={!canCreate}
                      title={createHint || undefined}
                    >
                      <Plus className="mr-1.5 h-4 w-4" /> Create Goal
                    </Button>
                  </div>
                  {!canCreate && createHint ? (
                    <span className="text-xs text-muted-foreground md:text-right">
                      {createHint}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {!selectedReport && plan?.canEdit ? (
              <PlanChecklist
                plan={plan}
                schedule={schedule}
                onSubmit={submitPlan}
                submitting={submittingPlan}
              />
            ) : null}

            <div className="space-y-2">
              {displayGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  readOnly={Boolean(selectedReport)}
                  canEditWeight={Boolean(displayPlan?.canEdit) && !selectedReport}
                  weightMax={schedule.maxWeight}
                  onWeightChange={(value) => updateWeight(goal.id, value)}
                  onOpen={() => setSelectedGoal(goal)}
                />
              ))}
              {!displayGoals.length ? (
                <PerformanceEmptyState
                  title="No goals yet"
                  body={
                    selectedReport
                      ? `${selectedReport.user.name} has not created goals.`
                      : createHint || 'Create 3–8 goals that total 100% weighting, then submit your plan.'
                  }
                  action={
                    !selectedReport && plan ? (
                      <Button
                        onClick={() => setNewGoalOpen(true)}
                        disabled={!canCreate}
                        title={createHint || undefined}
                      >
                        <Plus className="mr-2 h-4 w-4" /> Create Goal
                      </Button>
                    ) : undefined
                  }
                />
              ) : null}
            </div>
          </div>
        )
      ) : null}

      {tab === 'review' ? (
        <div className="space-y-4">
          {viewingTeam ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Choose a direct report
              </p>
              <div className="flex flex-wrap gap-1.5">
                {data.reports.map((report) => (
                  <button
                    key={report.user.id}
                    type="button"
                    onClick={() => selectReport(report, 'review')}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      selectedReport?.user.id === report.user.id
                        ? 'border-link/40 bg-selection text-link'
                        : 'border-border bg-card text-foreground hover:bg-accent'
                    )}
                  >
                    {report.user.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="inline-flex rounded-full bg-muted p-1">
            {(
              [
                ['mid', 'Mid-year'],
                ['eoy', 'End-of-year'],
              ] as [PeriodValue, string][]
            ).map(([value, label]) => {
              const window = value === 'mid' ? schedule.mid : schedule.eoy;
              return (
                <button
                  key={value}
                  type="button"
                  disabled={!window.enabled}
                  onClick={() => setPeriod(value)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                    period === value
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground',
                    !window.enabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {!periodEnabled ? (
            <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
              The {period === 'mid' ? 'mid-year' : 'end-of-year'} review is switched off for this
              cycle.
            </div>
          ) : !periodOpen ? (
            <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
              The {period === 'mid' ? 'mid-year' : 'end-of-year'} review opens{' '}
              {formatPerformanceDate(periodWindow.start)}. Keep goals and progress up to date
              until then.
            </div>
          ) : teamLoading ? (
            <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading review…
            </div>
          ) : (
            <ReviewPanel
              review={viewingTeam ? teamReview : ownReview}
              period={period}
              schedule={schedule}
              levels={data.ratingLevels}
              mode={viewingTeam ? 'manager' : 'self'}
              personName={reviewPersonName}
              userId={reviewPersonId}
              onChanged={reload}
            />
          )}

          {viewingTeam && !selectedReport ? (
            <p className="text-sm text-muted-foreground">
              Select a direct report above to open their review.
            </p>
          ) : null}
        </div>
      ) : null}

      {tab === 'touchpoints' ? (
        <TouchpointsPanel
          touchpoints={
            viewingTeam
              ? teamTouchpoints.filter((touchpoint) => {
                  const reportIds = new Set(data.reports.map((report) => report.user.id));
                  return touchpoint.userId != null && reportIds.has(touchpoint.userId);
                })
              : data.touchpoints
          }
          goals={viewingTeam ? [] : data.goals}
          schedule={schedule}
          manager={me.manager}
          meName={me.name}
          teamMode={viewingTeam}
          openSignal={bookingSignal}
          onChanged={async () => {
            await reload();
            if (data.canManageTeam) {
              const rows = await performanceApi.touchpoints.list(schedule.id);
              setTeamTouchpoints(rows);
            }
          }}
        />
      ) : null}

      <NewGoalDialog
        open={newGoalOpen}
        onOpenChange={setNewGoalOpen}
        schedule={schedule}
        categories={data.categories}
        departmentGoals={data.departmentGoals}
        goals={data.goals}
        onSubmit={async (payload) => {
          await performanceApi.goals.create({ scheduleId: schedule.id, ...payload });
          toast.success('Goal created');
          await reload();
        }}
      />

      <GoalDetailDialog
        open={Boolean(selectedGoal)}
        onOpenChange={(open) => {
          if (!open) setSelectedGoal(null);
        }}
        goal={
          selectedGoal
            ? (displayGoals.find((goal) => goal.id === selectedGoal.id) || selectedGoal)
            : null
        }
        plan={displayPlan}
        schedule={schedule}
        canEditProgress={!selectedReport && schedule.status === 'Active'}
        canWriteNote={schedule.status !== 'Closed'}
        onChanged={reload}
      />
    </div>
  );
}
